import * as _ from 'underscore';

import { SiteUrls } from '@tableau/shared-urls';

import { RefreshContentScope } from '../../app/ActionPlaceScopes';
import { updateBreadcrumbs } from '../../app/actions/UpdateBreadcrumbsAction';
import { appStore } from '../../app/AppStore';
import { watchUrlForBreadcrumbUpdates } from '../../app/content/breadcrumbs/BreadcrumbHelpers';
import { BreadcrumbStateInfo } from '../../app/content/breadcrumbs/BreadcrumbStateInfo';
import { SiteUsersGrid } from '../../app/content/SiteUsersGrid';
import { IMainScope } from '../../app/IMainScope';
import { IPlaceTitleScope } from '../../app/IPlaceTitleScope';
import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { AngularToReactDataGridBridge } from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { BuyLicensesTelemetryEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { Telemetry } from '../../ts/services/TelemetryService';
import { ListResult } from '../fetchers/ListResult';
import { ActionButtonsHelper, IActionsMenuScope, SubplaceWithActionButtonsScope } from '../helpers/ActionButtonsHelper';
import { ActionContext } from '../helpers/ActionContext';
import { SiteRoleFilterHelper } from '../helpers/SiteRoleFilterHelper';
import { SiteUserContentType } from '../models/ContentTypes';
import { IEmptyTemplateModel } from '../models/IEmptyTemplate';
import { ITabsBannerModel } from '../models/ITabsBannerModel';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { IResourceQuery } from '../resources/ResourceQuery';
import { SiteRoleOptionsResource } from '../resources/SiteRoleOptions';
import { SiteRolesResource } from '../resources/SiteRoles';
import { SiteUsers } from '../resources/SiteUsers';
import { ILicenseConsumptionInfo } from '../ServerApi/types/ILicenseConsumptionInfo';
import { SiteRoles } from '../ServerApi/types/ISiteRole';
import { ISiteUser } from '../ServerApi/types/ISiteUser';
import { IActionButton, IListActionButton } from '../services/ActionPanelService';
import { SyncGroupEvent } from '../services/ActiveDirectoryGroupAction';
import { AddUsersOptionsAction, UserAddedEvent } from '../services/AddUsersOptionsAction';
import { BrowserTitleService } from '../services/BrowserTitleService';
import { FilterPanelService, ISelectFilterDefinition } from '../services/FilterPanelService';
import { LicensesService } from '../services/LicensesService';
import { IQueryUpdater, QueryService } from '../services/QueryService';
import { ServerService } from '../services/ServerService';
import {
  ISiteAuthenticationSettings,
  SiteAuthenticationSettingsService,
} from '../services/SiteAuthenticationSettingsService';
import { SiteUsersActions } from '../services/SiteUsersActions';
import { SorterFactory } from '../services/SorterFactory';
import { WindowLocationService } from '../services/WindowLocationService';
import { ListActionsScope, SubplaceWithDataGridScope } from './ContentSubplaceCtrl';
import { IFilterPanelContainerScope } from './FilterPanelCtrl';
import { SiteRolesEvent } from './SiteMembershipDialogCtrl';

export interface ISiteUsersPlaceScope extends
  IActionsMenuScope<ISiteUser, {}>,
  IMainScope,
  IPlaceTitleScope,
  IFilterPanelContainerScope,
  ListActionsScope,
  RefreshContentScope,
  SubplaceWithActionButtonsScope<ISiteUser, {}>,
  SubplaceWithDataGridScope<ISiteUser>,
  IFilterPanelContainerScope
{
  model: ITabsBannerModel;
  multisite: boolean;
  server: ServerService;
  licenseConsumptionInfo: ILicenseConsumptionInfo;
  shouldShowSubscriptionLicensingInfoBar: boolean;
  isFetchingLicenseInfo: boolean;
  isCoreBasedLicense: boolean;
  isBuyLicensesEnabled: boolean;
  buyLicensesClicked: () => void;
}

export function getSiteUsersPlaceBreadcrumb($state: ng.ui.IStateService, multiSite: boolean): BreadcrumbStateInfo {
  return {
    id: $state.href('main.site.users'),
    text: multiSite ? Messages.allSiteUsers() : Messages.allUsers(),
    href: $state.href('main.site.users'),
    testId: 'site-users-link'
  };
}

export class SiteUsersPlaceCtrl {

  public static $inject = [
    '$location',
    '$scope',
    '$state',
    '$translate',
    'AngularToReactDataGridBridge',
    'AddUsersOptionsAction',
    'BrowserTitleService',
    'QueryService',
    'ServerService',
    'SiteRoles',
    'canSessionUserManageUsers', // from state router
    'siteAuthenticationSettings', // injected by state router
    'SiteAuthenticationSettingsService',
    'SiteUsers',
    'SiteUsersActions',
    'SorterFactory',
    'FilterPanelService',
    'LicensesService',
    'SiteRoleOptionsResource',
    'TelemetryService',
    'WindowLocationService'
  ];

  private siteRoleFilterDefinition: ISelectFilterDefinition<string>;

  private static emptyTemplateModel: IEmptyTemplateModel = { title: 'usersEmptyMessage' };

  constructor(
    private $location: ng.ILocationService,
    private $scope: ISiteUsersPlaceScope,
    private $state: ng.ui.IStateService,
    private $translate: ng.translate.ITranslateService,
    private AngularToReactDataGridBridge: AngularToReactDataGridBridge,
    addUsersOptionsAction: AddUsersOptionsAction,
    BrowserTitleService: BrowserTitleService,
    private queryService: QueryService,
    private serverService: ServerService,
    private siteRoles: SiteRolesResource,
    canSessionUserManageUsers: boolean,
    private siteAuthenticationSettings: ISiteAuthenticationSettings,
    private siteAuthenticationSettingsService: SiteAuthenticationSettingsService,
    private users: SiteUsers,
    siteUsersActions: SiteUsersActions,
    sorterFactory: SorterFactory,
    filterPanelService: FilterPanelService,
    private licensesService: LicensesService,
    private siteRoleOptionsResource: SiteRoleOptionsResource,
    private telemetryService: Telemetry,
    private windowLocationService: WindowLocationService
  ) {
    $scope.multisite = serverService.isSessionMultiSite();
    BrowserTitleService.setTitleWithPlace({translatablePlaceName: $scope.multisite ? 'siteUsers' : 'users'});

    $scope.model = { itemsCount: 0 };
    $scope.isFetchingLicenseInfo = false;
    $scope.shouldShowSubscriptionLicensingInfoBar = false;
    $scope.server = serverService;
    $scope.sorter = sorterFactory.create(SiteUserContentType.defaultOrder);
    $scope.selectedItems = new Set<ISiteUser>((user: ISiteUser) => user.id);

    const actions = siteUsersActions.getSiteUsersActionsButtons();
    const getActionTarget = ActionButtonsHelper.getActionTargetFn($scope, $scope.selectedItems);

    this.configureForReactDataGrid(actions, getActionTarget, siteRoleOptionsResource);

    ActionButtonsHelper.setupScopeForActionsMenuInActionBar({
      scope: $scope,
      isActionAvailable: action => action.isAvailable($scope.selectedItems),
      menuActions: actions,
      getActionTarget,
      translate: this.$translate.instant.bind(this.$translate)
    });

    $scope.listActions = [{
      name: 'add-users',
      translate: 'addUsers',
      isAvailable: () => canSessionUserManageUsers,
      execute: ($event: JQueryEventObject) => {
        addUsersOptionsAction.showAddSiteUsersOptionsDialog(ActionContext.fromEvent($event));
      }
    }];
    $scope.availableListAction = (listAction: IListActionButton) => listAction.isAvailable();

    $scope.filterControls = {};

    $scope.filterDefinitions = filterPanelService.getFilterDefinitions([ 'siteRole' ]);
    this.siteRoleFilterDefinition = <ISelectFilterDefinition<string>>$scope.filterDefinitions[0];

    const queryUpdater = this.createQueryUpdater();

    $scope.sliceChanged = (newSlice: ListResult<ISiteUser>) => {
      $scope.selectedItems.updateItems(newSlice.items);
      $scope.model.itemsCount = newSlice.total;
    };

    $scope.refreshContent = () => {
      queryUpdater.refresh();
      this.updateSiteLicenseConsumptionInfo();
    };

    $scope.refreshContent();

    $scope.placeTitleClicked = () => {
      $scope.refreshContent();
    };

    $scope.$on(UserAddedEvent, () => {
      $scope.refreshContent();
    });

    $scope.$on(SiteRolesEvent.SiteRolesChanged, () => {
      $scope.refreshContent();
    });

    $scope.$on(SyncGroupEvent.groupSynced, () => {
      $scope.refreshContent();
    });

    $scope.emptyTemplateModel = SiteUsersPlaceCtrl.emptyTemplateModel;

    this.initBreadcrumbs();
    this.initBuyLicenses();
  }

  private initBuyLicenses() {
    this.$scope.isBuyLicensesEnabled = this.serverService.isBuyLicensesEnabled();
    this.$scope.buyLicensesClicked = () => {
      this.telemetryService.sendEvent<BuyLicensesTelemetryEvent>({
        type: TelemetryEvents.BuyLicenses,
        action: 'buy-licenses-button-clicked',
      });
      this.windowLocationService.open('https://www.tableau.com/about/contact#contact_sales');
    };

    if (this.$scope.isBuyLicensesEnabled) {
      this.telemetryService.sendEvent<BuyLicensesTelemetryEvent>({
        type: TelemetryEvents.BuyLicenses,
        action: 'buy-licenses-button-displayed',
      });
    }
  }

  private updateSiteRoleCounts(baseQuery: IResourceQuery<ISiteUser>) {
    this.users.getUserCountsBySiteRole(baseQuery).then((userCountsByRole: _.Dictionary<number>) => {
      const allSiteRolesExceptGuest = _.without(this.siteRoles.allOptions(), SiteRoles.Guest);
      this.siteRoleFilterDefinition.options =
        SiteRoleFilterHelper.siteRoleOptions(allSiteRolesExceptGuest, this.siteRoleOptionsResource, userCountsByRole);
    });
  }

  private getUserAuthenticationInfo(user: ISiteUser): {name: string, isEnabled: boolean} {
    const provider = this.siteAuthenticationSettingsService.findProvider(this.siteAuthenticationSettings, user.authenticationProviderId);
    if (provider) {
      return {
        name: this.siteAuthenticationSettingsService.getProviderDisplayName(provider),
        isEnabled: provider.isEnabled
      };
    } else {
      return {
        name: this.$translate.instant('unspecified'),
        isEnabled: false
      };
    }
  }

  private updateSiteLicenseConsumptionInfo = () => {
    this.licensesService.hasSiteLicensingConsumptionInfo().then((hasSiteLicensingInfo: boolean) => {
      const showLicenseInfoBarForSingleSiteServer = !this.serverService.isSessionMultiSite() && this.serverService.shouldShowServerLicensingTierInfo();
      if ((hasSiteLicensingInfo === true) || showLicenseInfoBarForSingleSiteServer) {
        this.$scope.shouldShowSubscriptionLicensingInfoBar = true;
        this.$scope.isFetchingLicenseInfo = true;
        const licensingCapacityInfoPromise = (hasSiteLicensingInfo === true) ? this.licensesService.fetchSiteScopedCapacityInfoForInfoBar() : this.licensesService.fetchCapacityInfoForInfoBar();
        this.processLicenseConsumptionInfo(licensingCapacityInfoPromise);
      }
    });
  }

  private processLicenseConsumptionInfo(licensingCapacityInfoPromise: ng.IPromise<ILicenseConsumptionInfo>){
    licensingCapacityInfoPromise.then((licenseInfo: ILicenseConsumptionInfo) => {
      this.$scope.licenseConsumptionInfo = licenseInfo;
      this.$scope.isCoreBasedLicense = licenseInfo.totalCoreLicenses > 0;
    }).finally(() => {
      this.$scope.isFetchingLicenseInfo = false;
    });
  }

  private configureForReactDataGrid(actions: IActionButton<ISiteUser, {}>[],
                                    getActionTarget: () => IActionTarget<ISiteUser, {}>,
                                    siteRoleOptionsResource: SiteRoleOptionsResource) {
    const columns = SiteUsersGrid.columns({
      actions: actions,
      getActionTarget: getActionTarget,
      getUserAuthenticationInfo: (user: ISiteUser) => this.getUserAuthenticationInfo(user),
      scope: this.$scope,
      showAuthenticationColumn: this.siteAuthenticationSettingsService.isEnabled(),
      showDomainNameColumn: this.serverService.isServerDomainFamilyActiveDirectory(),
      siteUrls: new SiteUrls({siteUrlName: this.serverService.sessionSiteUrlName(), queryString: window.location.search}),
      translate: (key: string) => this.$translate.instant(key),
      siteRoleOptionsResource: siteRoleOptionsResource
    });

    this.AngularToReactDataGridBridge.attachReactDataGridToScope({
      $scope: this.$scope,
      columns,
      emptyChild: this.AngularToReactDataGridBridge.emptyTemplateElementFor(SiteUsersPlaceCtrl.emptyTemplateModel),
      canShowFilterPanel: SiteUserContentType.canShowFilterPanel,
      headerHeight: CommonStyles.Sizes.DataGridWithPictureIconsHeaderHeight,
      rowOuterHeight: CommonStyles.Sizes.DataGridWithPictureIconsRowHeight
    });
  }

  private createQueryUpdater(): IQueryUpdater {
    return this.queryService.createQueryUpdater(
      this.$scope,
      SiteUserContentType,
      this.users,
      (resourceQuery) => {
        this.AngularToReactDataGridBridge.updateReactDataGridRowStore({ scope: this.$scope, sliceable: resourceQuery });
        this.$scope.sliceable = resourceQuery;
        this.$scope.sorter.setOrder(resourceQuery.getOrder());
        this.updateSiteRoleCounts(resourceQuery);
        resourceQuery.getCount().then((result: number) => {
          this.$scope.model.itemsCount = result;
        });
      },
      {}
    );
  }

  private initBreadcrumbs() {
    const breadcrumbs: BreadcrumbStateInfo[] = [
      getSiteUsersPlaceBreadcrumb(this.$state, this.serverService.isSessionMultiSite())
    ];
    appStore.dispatch(updateBreadcrumbs(breadcrumbs));

    const currentPath = this.$location.path();
    watchUrlForBreadcrumbUpdates(this.$scope, this.$location, () => this.$location.path() === currentPath);
  }

}

Module.instance().controller('SiteUsersPlaceCtrl', SiteUsersPlaceCtrl);
